// Redaction at the source (docs/10 §3). Nothing in `diag/` builds a field by
// interpolating a caller's string: every value that reaches a report comes out
// of one of the coercions below, so "a token cannot be in a report" is a
// property of the type constructors rather than of a scrubbing pass at the end.
//
// The house rule is ALLOW-lists only. A deny-list is a list of the leaks we
// happened to think of; the endpoint list genuinely carries `kind: 'ip'`
// entries (protocol/endpoints.ts) and REST follows the signaling winner, so raw
// addresses really do arrive here — and "no tokens, IPs, or UUIDs in logs"
// (CLAUDE.md) admits no exception for a public one.
//
// `safeHost` is deliberately the same allow-list as `lib/http.ts`'s private
// `hostOf` (same LABEL/DNS_NAME regexes, same `[ip]`/`[host]` stand-ins). It is
// duplicated rather than imported only because `lib/http.ts` does not export it
// — see the cross-boundary note in this job's report; when it does, this should
// collapse onto that one definition.

/** Stand-ins for a host we may not print. Naming the TIER is the useful half of
 *  the diagnosis ("we are down to the IP fallback") and leaks no address. */
export const IP_HOST = '[ip]';
export const OPAQUE_HOST = '[host]';
/** A same-origin/relative base has no host at all — not a redaction. */
export const RELATIVE_HOST = '[relative]';

const LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
// The TLD must start with letters, which is what excludes every numeric host.
// `xn--` is spelled out because an IDN mirror (a .рф domain is xn--p1ai) has
// digits in its TLD and must still be nameable — a censored user's report has
// to say WHICH mirror died.
const DNS_NAME = new RegExp(`^${LABEL}(?:\\.${LABEL})*\\.(?:[a-z]{2,}|xn--[a-z0-9-]{2,})$`, 'i');

/**
 * A second SHAPE allow-list on top of the TLD one, because a valid DNS name is
 * not automatically a safe one: `6f1c1f2e-8a3b-4d5e-9f01-2233445566aa.merkury.app`
 * passes every syntactic test for a hostname and is a UUID in a report. Real
 * endpoint names are short and wordlike (`eu1.merkury.app`), so requiring that
 * shape costs nothing we want and rejects the id-bearing forms. Still an
 * allow-list: anything outside these bounds collapses to `[host]`.
 */
const MAX_LABEL_CHARS = 24;
const MAX_LABELS = 6;
const MAX_HOST_CHARS = 64;

function isPublishableShape(host: string): boolean {
  if (host.length > MAX_HOST_CHARS) return false;
  const labels = host.split('.');
  if (labels.length > MAX_LABELS) return false;
  return labels.every((label) => label.length <= MAX_LABEL_CHARS);
}

function looksLikeAddress(host: string): boolean {
  return host.includes(':') || host.startsWith('[') || /^[0-9.]+$/.test(host);
}

/**
 * A hostname safe to publish, or a stand-in. Accepts a bare host, `host:port`,
 * or an IPv6 authority. Every spelling of an address fails DNS_NAME: a dotted
 * quad ends in digits, IPv6 carries `:`/brackets, and the decimal/hex/octal
 * forms a URL parser accepts have no dot at all. Anything that fails is
 * REPLACED, never abbreviated, so no octet can reach a report by construction.
 */
export function safeHost(raw: unknown): string {
  if (typeof raw !== 'string') return OPAQUE_HOST;
  const trimmed = raw.trim().replace(/\.$/, '');
  if (trimmed === '') return OPAQUE_HOST;
  const host = trimmed.startsWith('[') ? trimmed : trimmed.replace(/:\d+$/, '');
  if (DNS_NAME.test(host) && isPublishableShape(host)) return host.toLowerCase();
  return looksLikeAddress(host) ? IP_HOST : OPAQUE_HOST;
}

/** Scheme + authority of an absolute URL; `[^/?#]+` stops before the path. */
const AUTHORITY_RE = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i;

/**
 * The host an absolute URL names. Hand-rolled rather than `new URL` because the
 * iOS brain runs in a bare JSContext with no URL constructor. Userinfo is
 * stripped before the host test — credentials never reach a report either.
 */
export function safeHostFromUrl(raw: unknown): string {
  if (typeof raw !== 'string') return OPAQUE_HOST;
  const url = raw.trim();
  if (url === '') return OPAQUE_HOST;
  if (url.startsWith('/')) return RELATIVE_HOST;
  const match = AUTHORITY_RE.exec(url);
  if (!match) return safeHost(url);
  return safeHost((match[1] as string).replace(/^[^@]*@/, ''));
}

/**
 * Path segments that are part of the API's public route vocabulary and carry no
 * identity. An ALLOW-list, so an id-bearing segment cannot be missed: anything
 * not named here becomes `{x}`, including the segments that look innocuous.
 * (A UUID is not reliably recognisable — `aaaaaaaa-1111-4111-8111-…` is a
 * perfectly good lowercase word by any pattern loose enough to keep `requests`.)
 */
const ROUTE_SEGMENTS: ReadonlySet<string> = new Set([
  'api',
  'v1',
  'auth',
  'challenge',
  'passkeys',
  'login',
  'options',
  'register',
  'password',
  'refresh',
  'verify',
  'me',
  'aliases',
  'friends',
  'requests',
  'accept',
  'decline',
  'blocks',
  'block',
  'unblock',
  'sessions',
  'current',
  'others',
  'calls',
  'seen',
  'turn',
  'credentials',
  'endpoints',
  'diag',
  'reports',
  'push',
  'avatar',
  'ws',
]);

/** Opaque stand-in for any path segment outside the route vocabulary. */
export const OPAQUE_SEGMENT = '{x}';

/**
 * A path TEMPLATE — the route, never the instance. The query string is dropped
 * whole (it can carry anything) and every unrecognised segment collapses, so
 * `/friends/6f1…/accept` reports as `/friends/{x}/accept`.
 */
export function pathTemplate(raw: unknown): string {
  if (typeof raw !== 'string') return '/';
  // An absolute URL is accepted so a call site can pass what it already has;
  // the scheme and authority are dropped, not turned into segments.
  const withoutOrigin = raw.replace(AUTHORITY_RE, '');
  const path = (withoutOrigin.split('#')[0] ?? '').split('?')[0] ?? '';
  const segments = path.split('/').filter((s) => s !== '');
  if (segments.length === 0) return '/';
  return `/${segments.map((s) => (ROUTE_SEGMENTS.has(s.toLowerCase()) ? s.toLowerCase() : OPAQUE_SEGMENT)).join('/')}`;
}

/**
 * Country and carrier are the most diagnostically valuable fields and the most
 * identifying, so §3 pairs them with COARSE timestamps: events to the second,
 * batches to the minute. Carrier is low-entropy on its own; carrier plus
 * millisecond timestamps is a fingerprint, and the coarsening is what keeps the
 * pair safe. Both return UNIX SECONDS — a report never carries a millisecond.
 */
export function coarseSecond(ms: number): number {
  return Math.floor(nonNegative(ms) / 1000);
}

export function coarseMinute(ms: number): number {
  return Math.floor(nonNegative(ms) / 60_000) * 60;
}

function nonNegative(v: number): number {
  return Number.isFinite(v) && v > 0 ? v : 0;
}

/** One of `allowed`, or `fallback`. The single gate every enum field passes. */
export function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/** A bounded non-negative integer. Durations and counters only — never a clock. */
export function count(value: unknown, cap = 1_000_000_000): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return Math.min(Math.round(value), cap);
}

/** ISO 3166-1 alpha-2, upper-cased, or null. Shape allow-list, not a country list. */
export function safeCountry(value: unknown): string | null {
  return typeof value === 'string' && /^[A-Za-z]{2}$/.test(value) ? value.toUpperCase() : null;
}

/** MCC+MNC as digits (`25001`), or null. Never `getSubscriberId` — that names
 *  the SUBSCRIBER, not the operator, and is out of bounds (§3). */
export function safeCarrier(value: unknown): string | null {
  return typeof value === 'string' && /^[0-9]{5,6}$/.test(value) ? value : null;
}

/**
 * App/OS version strings, narrowed to the shapes a version actually takes: at
 * most three dot-separated components, plus an optional `-alpha`/`+build` tail.
 *
 * The third dot is the point. A FOUR-component numeric version (`1.2.3.4`, which
 * Android `versionName` permits) is indistinguishable from a dotted quad, and
 * `containsResidue`'s deliberately over-inclusive IPv4 rule would then drop
 * every report that build ever cuts — silently, forever. Refusing the shape here
 * costs one field on an unusual build; letting it through costs the whole
 * diagnostics feature on it.
 */
export function safeVersion(value: unknown, max = 24): string | null {
  return typeof value === 'string' &&
    /^[0-9A-Za-z][0-9A-Za-z]*(?:\.[0-9A-Za-z]+){0,2}(?:[-+][0-9A-Za-z.\-+]*)?$/.test(value) &&
    value.length <= 63
    ? value.slice(0, max)
    : null;
}

/** A build commit: short or full lowercase hex, or null. */
export function safeCommit(value: unknown): string | null {
  return typeof value === 'string' && /^[0-9a-f]{7,40}$/.test(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Last line of defence
// ---------------------------------------------------------------------------

/** One 16-bit group — the atom every IPv6 spelling is built from. */
const HEX16 = '[0-9a-f]{1,4}';

/**
 * IPv6, and the only residue rule that is a broken PROMISE if it is too narrow
 * and a broken FEATURE if it is too wide. Log lines carry clock times
 * constantly, and a clock is colon-separated groups of hex-looking digits too;
 * docs/10 §5 makes the copy/paste rung the floor of the delivery ladder — the
 * one that always works, and the rung the 2026-07-26 incident was actually
 * diagnosed through — so a gate that blanks every line with a time on it does
 * not produce a safer report, it produces no report, and the user pastes
 * something unredacted from somewhere else instead.
 *
 * The discrimination is GRAMMATICAL, not a heuristic, which is why both halves
 * hold. A real address is either EIGHT groups or carries a `::`; `HH:MM`,
 * `HH:MM:SS`, `HH:MM:SS.mmm`, SMPTE `HH:MM:SS:FF` and the time half of an
 * ISO-8601 stamp are at most FOUR groups and never compress. The branches:
 *
 *  - 5–8 uncompressed groups. Above anything a clock or a timecode can spell,
 *    so no time reaches it, while a full address — and a MAC-shaped run, which
 *    is an identifier too — still does.
 *  - `::` with at least one group AFTER it, and zero or more before. The zero
 *    is the point: the old `{2,7}` demanded two groups ahead of the tail, so
 *    every single-leading-group address (`fe80::1`, `2606::1`, `::1`) walked
 *    straight through the gate.
 *  - a trailing `::`, which needs TWO groups in front of it. `<≤4 hex chars>::`
 *    is also how a qualified name reads in a native crash line (`std::`,
 *    `a::b`), and a bare prefix names a network rather than a device, so the
 *    second group costs nothing identifying and buys back those lines.
 *
 * Brackets and zone ids need no branch: this is a substring gate, and
 * `[fe80::1%en0]:8443` contains `fe80::1`. The boundaries refuse a match that
 * starts or ends inside a word, which is what keeps `::before` and `Vec::new`
 * whole; `:` is allowed before, so `ip:fe80::1` still trips.
 *
 * The trailing guard rejects a colon only when it CONTINUES the address. An
 * unconditional `(?![0-9a-z:])` looks equivalent and is not: it silently kept
 * `ICE failed for 2606:4700:4700::1111: timeout` — a real routable address
 * followed by an ordinary punctuation colon — because the delimiter looked
 * like more address. Prose puts a colon after a value constantly, so that
 * spelling leaked exactly where a log line is most likely to name one.
 */
const IPV6 = new RegExp(
  '(?:^|[^0-9a-z])(?:' +
    `(?:${HEX16}:){4,7}${HEX16}` +
    `|(?:${HEX16}(?::${HEX16}){0,6})?::${HEX16}(?::${HEX16}){0,6}` +
    `|${HEX16}(?::${HEX16}){1,6}::` +
    ')(?![0-9a-z])(?!:[0-9a-f])',
  'i',
);

/**
 * Shapes that must never survive into an upload. This is a GATE, not the
 * mechanism: the constructors above are what keep these out, and a report that
 * still trips this is dropped rather than scrubbed — a missing report is a
 * nuisance, a leaked one is a broken promise (docs/10 §9).
 *
 * The token pattern deliberately spares runs of pure lowercase hex, because a
 * 40-char git sha is `brainCommit`, which §3 collects on purpose. Real
 * credentials are JWTs or mixed-case base64url and are caught either way.
 */
const RESIDUE: readonly RegExp[] = [
  // UUID, any version.
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  // Dotted-quad IPv4 with in-range octets (so `0.55.1.2`-style versions that
  // are not addresses still trip it — deliberately over-inclusive here).
  /\b(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\b/,
  IPV6,
  // JWT.
  /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]*/,
  // Credential-looking opaque runs, minus the lowercase-hex carve-out above.
  /\b(?![0-9a-f]{32,}\b)[A-Za-z0-9_-]{32,}\b/,
  // A bearer credential however it was spelled.
  /\bBearer\s+\S+/i,
];

/** True when `text` still contains something §3 forbids. */
export function containsResidue(text: string): boolean {
  return RESIDUE.some((re) => re.test(text));
}
